import { useState } from "react";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { PublicKey, Transaction } from "@solana/web3.js";
import {
    createTransferCheckedInstruction,
    getAssociatedTokenAddress,
    getAccount,
    createAssociatedTokenAccountInstruction,
    TOKEN_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const USDC_DECIMALS = 6;

export default function CryptoCheckoutButton({ cryptoConfig, onSuccess }) {
    const { connection } = useConnection();
    const { publicKey, sendTransaction, connected } = useWallet();
    const [busy, setBusy] = useState(false);

    const treasury = cryptoConfig.treasury;
    const mint = cryptoConfig.usdc_mint;
    const amount = cryptoConfig.amount_usdc;
    const cluster = cryptoConfig.cluster;

    async function handlePay() {
        if (!publicKey) {
            toast.error("Connect your wallet first.");
            return;
        }
        setBusy(true);
        try {
            const mintPk = new PublicKey(mint);
            const treasuryPk = new PublicKey(treasury);
            const fromAta = await getAssociatedTokenAddress(mintPk, publicKey);
            const toAta = await getAssociatedTokenAddress(mintPk, treasuryPk);

            const tx = new Transaction();
            // If treasury's ATA doesn't exist, create it (payer = sender)
            try {
                await getAccount(connection, toAta);
            } catch (_) {
                tx.add(
                    createAssociatedTokenAccountInstruction(
                        publicKey,
                        toAta,
                        treasuryPk,
                        mintPk,
                        TOKEN_PROGRAM_ID,
                        ASSOCIATED_TOKEN_PROGRAM_ID,
                    )
                );
            }

            const amountBase = Math.round(amount * 10 ** USDC_DECIMALS);
            tx.add(
                createTransferCheckedInstruction(
                    fromAta,
                    mintPk,
                    toAta,
                    publicKey,
                    amountBase,
                    USDC_DECIMALS,
                )
            );

            toast.info("Confirm in your wallet…");
            const signature = await sendTransaction(tx, connection);
            toast.info("Transaction sent. Confirming on-chain…");

            // Wait up to ~60s for confirmation
            const latestBlockhash = await connection.getLatestBlockhash();
            await connection.confirmTransaction(
                { signature, ...latestBlockhash },
                "confirmed"
            );

            toast.info("Confirmed. Verifying with server…");
            const { data } = await api.post("/payments/crypto/verify", { signature });
            if (data.status === "COMPLETED") {
                toast.success("Boarding complete. Welcome, Privateer.");
                onSuccess({
                    status: "COMPLETED",
                    capture_id: signature,
                    amount_usd: data.amount_usdc,
                    split: data.split,
                    method: "crypto",
                });
            } else {
                toast.error(`Unexpected status: ${data.status}`);
            }
        } catch (err) {
            const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message || "Transaction cancelled";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    }

    const networkLabel = cluster === "mainnet-beta" ? "Mainnet" : cluster;
    const isMainnet = cluster === "mainnet-beta";

    return (
        <div data-testid="crypto-checkout" className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div>
                    <div className="caption" style={{ color: "var(--brand-accent)" }}>
                        Solana · USDC · {networkLabel}
                    </div>
                    <p className="mt-1 text-sm text-[#FAFAFA]">
                        Pay <span className="font-mono">{amount.toFixed(2)} USDC</span>{" "}
                        <span className="text-[var(--brand-accent)]">(Sovereign Discount · {cryptoConfig.discount_pct}% off)</span>
                    </p>
                    <p className="mt-0.5 text-xs text-[#737373] break-all">
                        → <span className="font-mono">{treasury}</span>
                    </p>
                </div>
                <WalletMultiButton
                    data-testid="wallet-connect-btn"
                    style={{
                        backgroundColor: "transparent",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "2px",
                        height: "40px",
                        fontSize: "13px",
                    }}
                />
            </div>

            {isMainnet && (
                <div className="rounded-sm border border-[#7A2F2F]/40 bg-[#7A2F2F]/10 p-3 text-xs text-[#FAFAFA]/90">
                    <span className="font-semibold">Mainnet active.</span> Real USDC will leave your wallet.
                </div>
            )}

            <button
                data-testid="btn-pay-crypto"
                onClick={handlePay}
                disabled={!connected || busy}
                className="rounded-sm py-3.5 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.35)] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
            >
                {!connected ? "Connect wallet to continue" : busy ? "Processing on-chain…" : `Pay ${amount.toFixed(2)} USDC — One Click`}
            </button>
        </div>
    );
}
